import React, { useState, useEffect } from 'react';
import { Modal, Form, Button, Row, Col, Alert, Spinner, Table } from 'react-bootstrap';
import { oneKlicks } from '../../../SDK/oneclick';
import { getuserdata } from '../../../services/apiService';
import { toast } from 'react-toastify';
import ImageShow from '../../../Uitiles/ImageShow';

const AddAttendanceModal = ({ show, onHide, onSuccess, selectedDate = null, editingAttendance = null }) => {
    const [formData, setFormData] = useState({
        employee_id: '',
        date: '',
        check_in: '',
        check_out: '',
        break_start: '',
        break_end: '',
        total_hours: '',
        overtime_hours: '',
        status: 'present',
        notes: ''
    });

    const [employeeList, setEmployeeList] = useState([]);
    const [selectedEmployee, setSelectedEmployee] = useState(null);
    const [loading, setLoading] = useState(false);
    const [errors, setErrors] = useState({});
    const [bulkMode, setBulkMode] = useState(false);
    const [bulkData, setBulkData] = useState([]);

    const attendanceStatuses = [
        { value: 'present', label: 'Present', color: 'success' },
        { value: 'absent', label: 'Absent', color: 'danger' },
        { value: 'late', label: 'Late', color: 'warning' },
        { value: 'half_day', label: 'Half Day', color: 'info' },
        { value: 'leave', label: 'Leave', color: 'secondary' },
        { value: 'holiday', label: 'Holiday', color: 'primary' }
    ];

    useEffect(() => {
        if (show) {
            fetchEmployeeList();
            if (editingAttendance) {
                setFormData({
                    employee_id: editingAttendance.employee_id || '',
                    date: editingAttendance.date || '',
                    check_in: editingAttendance.check_in || '',
                    check_out: editingAttendance.check_out || '',
                    break_start: editingAttendance.break_start || '',
                    break_end: editingAttendance.break_end || '',
                    total_hours: editingAttendance.total_hours || '',
                    overtime_hours: editingAttendance.overtime_hours || '',
                    status: editingAttendance.status || 'present',
                    notes: editingAttendance.notes || ''
                });
                if (editingAttendance.employee_id) {
                    setSelectedEmployee(employeeList.find(emp => emp.id === editingAttendance.employee_id));
                }
            } else {
                resetForm();
                if (selectedDate) {
                    setFormData(prev => ({ ...prev, date: selectedDate }));
                } else {
                    setFormData(prev => ({ ...prev, date: new Date().toISOString().split('T')[0] }));
                }
            }
        }
    }, [show, editingAttendance, selectedDate]);

    useEffect(() => {
        if (formData.employee_id) {
            const employee = employeeList.find(emp => emp.id === parseInt(formData.employee_id));
            if (employee) {
                setSelectedEmployee(employee);
            }
        }
    }, [formData.employee_id, employeeList]);

    useEffect(() => {
        // Initialize bulk data when switching to bulk mode
        if (bulkMode && employeeList.length > 0) {
            const initialBulkData = employeeList.map(emp => ({
                employee_id: emp.id,
                employee_name: `${emp.staff?.[0]?.first_name} ${emp.staff?.[0]?.last_name}`,
                date: formData.date || new Date().toISOString().split('T')[0],
                check_in: '',
                check_out: '',
                status: 'present',
                notes: ''
            }));
            setBulkData(initialBulkData);
        } else if (!bulkMode) {
            // Clear bulk data when switching to single mode
            setBulkData([]);
        }
    }, [bulkMode, employeeList, formData.date]);

    useEffect(() => {
        // Auto-calculate total hours with improved logic
        if (formData.check_in && formData.check_out) {
            const checkIn = new Date(`${formData.date}T${formData.check_in}`);
            const checkOut = new Date(`${formData.date}T${formData.check_out}`);
            
            // Handle overnight shifts (check-out next day)
            if (checkOut <= checkIn) {
                checkOut.setDate(checkOut.getDate() + 1);
            }
            
            if (checkOut > checkIn) {
                let totalMs = checkOut - checkIn;
                
                // Subtract break time if provided
                if (formData.break_start && formData.break_end) {
                    const breakStart = new Date(`${formData.date}T${formData.break_start}`);
                    const breakEnd = new Date(`${formData.date}T${formData.break_end}`);
                    
                    // Handle break time that might span midnight
                    if (breakEnd <= breakStart) {
                        breakEnd.setDate(breakEnd.getDate() + 1);
                    }
                    
                    const breakMs = breakEnd - breakStart;
                    totalMs -= breakMs;
                }
                
                const totalHours = totalMs / (1000 * 60 * 60);
                const standardHours = 8; // Standard working hours
                const overtime = Math.max(0, totalHours - standardHours);
                
                setFormData(prev => ({
                    ...prev,
                    total_hours: totalHours.toFixed(2),
                    overtime_hours: overtime.toFixed(2)
                }));
            } else {
                // Reset if invalid time range
                setFormData(prev => ({
                    ...prev,
                    total_hours: '',
                    overtime_hours: ''
                }));
            }
        } else {
            // Reset if either time is missing
            setFormData(prev => ({
                ...prev,
                total_hours: '',
                overtime_hours: ''
            }));
        }
    }, [formData.check_in, formData.check_out, formData.break_start, formData.break_end, formData.date]);

    const fetchEmployeeList = async () => {
        try {
            const tableSchema = '';
            const tableName = 'employees';
            const conditions = {
                status: 'active'
            };
            const orderBy = 'id DESC';
            const search = {};
            const pagination = {};
            const margedata = [
                {
                    target_table: 'users',
                    target_column: 'id',
                    target_value: 'staff_id',
                    target_label: 'staff'
                }
            ];

            const response = await oneKlicks.read(tableSchema, tableName, conditions, orderBy, search, pagination, margedata);
            
            if (response.success) {
                setEmployeeList(response.data);
            } else {
                toast.error('Failed to fetch employee list');
            }
        } catch (error) {
            console.error('Error fetching employee list:', error);
            toast.error('Error fetching employee list');
        }
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
        
        // Clear error when user starts typing
        if (errors[name]) {
            setErrors(prev => ({
                ...prev,
                [name]: ''
            }));
        }
    };

    const handleBulkInputChange = (index, field, value) => {
        const updatedBulkData = [...bulkData];
        updatedBulkData[index][field] = value;
        setBulkData(updatedBulkData);
    };

    const validateForm = () => {
        const newErrors = {};

        if (!formData.employee_id) {
            newErrors.employee_id = 'Please select an employee';
        }
        if (!formData.date) {
            newErrors.date = 'Please select a date';
        }
        if (formData.status === 'present' && !formData.check_in) {
            newErrors.check_in = 'Check-in time is required for present status';
        }

        // Validate time logic
        if (formData.check_in && formData.check_out) {
            const checkIn = new Date(`${formData.date}T${formData.check_in}`);
            const checkOut = new Date(`${formData.date}T${formData.check_out}`);
            
            // Handle overnight shifts
            if (checkOut <= checkIn) {
                checkOut.setDate(checkOut.getDate() + 1);
            }
            
            const totalMs = checkOut - checkIn;
            const totalHours = totalMs / (1000 * 60 * 60);
            
            // Check for reasonable working hours (not more than 24 hours)
            if (totalHours > 24) {
                newErrors.check_out = 'Working hours cannot exceed 24 hours';
            }
            
            // Check for minimum working hours (at least 1 hour)
            if (totalHours < 1) {
                newErrors.check_out = 'Working hours must be at least 1 hour';
            }
        }

        // Validate break times
        if (formData.break_start && formData.break_end) {
            const breakStart = new Date(`${formData.date}T${formData.break_start}`);
            const breakEnd = new Date(`${formData.date}T${formData.break_end}`);
            
            if (breakEnd <= breakStart) {
                breakEnd.setDate(breakEnd.getDate() + 1);
            }
            
            const breakMs = breakEnd - breakStart;
            const breakHours = breakMs / (1000 * 60 * 60);
            
            if (breakHours > 4) {
                newErrors.break_end = 'Break time cannot exceed 4 hours';
            }
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const validateBulkForm = () => {
        const errors = [];
        
        bulkData.forEach((item, index) => {
            if (item.status === 'present' && !item.check_in) {
                errors.push(`Employee ${item.employee_name}: Check-in time is required for present status`);
            }
        });

        if (errors.length > 0) {
            toast.error(errors.join('\n'));
            return false;
        }
        return true;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (bulkMode) {
            if (!validateBulkForm()) return;
            await handleBulkSubmit();
        } else {
            if (!validateForm()) {
                toast.error('Please fix the errors before submitting');
                return;
            }
            await handleSingleSubmit();
        }
    };

    const handleSingleSubmit = async () => {
        setLoading(true);
        try {
            const userId = await getuserdata('id');
            
            const attendanceData = {
                employee_id: parseInt(formData.employee_id),
                date: formData.date,
                check_in: formData.check_in || null,
                check_out: formData.check_out || null,
                break_start: formData.break_start || null,
                break_end: formData.break_end || null,
                total_hours: parseFloat(formData.total_hours) || 0,
                overtime_hours: parseFloat(formData.overtime_hours) || 0,
                status: formData.status,
                notes: formData.notes || null,
                created_by: userId
            };

            const tableSchema = '';
            const tableName = 'attendance';
            
            let response;
            if (editingAttendance) {
                // Update existing attendance
                const conditions = { id: editingAttendance.id };
                response = await oneKlicks.update(tableSchema, tableName, attendanceData, conditions);
            } else {
                // Create new attendance
                const conditions = {};
                response = await oneKlicks.create(tableSchema, tableName, attendanceData, conditions);
            }

            if (response.success) {
                toast.success(editingAttendance ? 'Attendance updated successfully' : 'Attendance added successfully');
                onSuccess && onSuccess(response.data);
                onHide();
            } else {
                toast.error(response.message || 'Failed to save attendance');
            }
        } catch (error) {
            console.error('Error saving attendance:', error);
            toast.error('Error saving attendance');
        } finally {
            setLoading(false);
        }
    };

    const handleBulkSubmit = async () => {
        setLoading(true);
        try {
            const userId = await getuserdata('id');
            
            const attendancePromises = bulkData.map(attendance => {
                const attendanceData = {
                    employee_id: parseInt(attendance.employee_id),
                    date: attendance.date,
                    check_in: attendance.check_in || null,
                    check_out: attendance.check_out || null,
                    status: attendance.status,
                    notes: attendance.notes || null,
                    created_by: userId
                };

                const tableSchema = '';
                const tableName = 'attendance';
                const conditions = {};
                
                return oneKlicks.create(tableSchema, tableName, attendanceData, conditions);
            });

            const responses = await Promise.all(attendancePromises);
            const successCount = responses.filter(r => r.success).length;
            
            if (successCount === bulkData.length) {
                toast.success(`Bulk attendance added successfully for ${successCount} employees`);
                onSuccess && onSuccess();
                onHide();
            } else {
                toast.warning(`Attendance added for ${successCount} out of ${bulkData.length} employees`);
            }
        } catch (error) {
            console.error('Error saving bulk attendance:', error);
            toast.error('Error saving bulk attendance');
        } finally {
            setLoading(false);
        }
    };

    const resetForm = () => {
        setFormData({
            employee_id: '',
            date: '',
            check_in: '',
            check_out: '',
            break_start: '',
            break_end: '',
            total_hours: '',
            overtime_hours: '',
            status: 'present',
            notes: ''
        });
        setSelectedEmployee(null);
        setErrors({});
    };

    return (
        <Modal show={show} onHide={onHide} size={bulkMode ? "lg" : "lg"} centered>
            <Modal.Header>
                <Modal.Title>
                    {editingAttendance ? 'Edit Attendance' : 
                     bulkMode ? 'Bulk Attendance Entry' : 'Add Attendance'}
                </Modal.Title>
                <div className="d-flex gap-2 ms-auto">
                    <Button
                        variant={bulkMode ? "outline-secondary" : "secondary"}
                        onClick={() => setBulkMode(false)}
                        disabled={!bulkMode}
                        style={{ minWidth: 100 }}
                    >
                        Single Entry
                    </Button>
                    <Button
                        variant={bulkMode ? "secondary" : "outline-secondary"}
                        onClick={() => setBulkMode(true)}
                        disabled={bulkMode}
                        style={{ minWidth: 100 }}
                    >
                        Bulk Entry
                    </Button>
                    <Button
                        variant="outline-danger"
                        onClick={onHide}
                        style={{ minWidth: 80 }}
                    >
                        Close
                    </Button>
                </div>
            </Modal.Header>
            <Modal.Body style={{ maxHeight: '70vh', overflowY: 'auto' }}>
                {!bulkMode ? (
                    <Form onSubmit={handleSubmit}>
                        <Row>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Select Employee *</Form.Label>
                                    <Form.Select
                                        name="employee_id"
                                        className="form-control"
                                        value={formData.employee_id}
                                        onChange={handleInputChange}
                                        isInvalid={!!errors.employee_id}
                                    >
                                        <option value="">Choose employee...</option>
                                        {employeeList.map(employee => (
                                            <option key={employee.id} value={employee.id}>
                                                {employee.staff?.[0]?.first_name} {employee.staff?.[0]?.last_name}
                                                {employee.employee_id && ` (${employee.employee_id})`}
                                            </option>
                                        ))}
                                    </Form.Select>
                                    {errors.employee_id && (
                                        <Form.Control.Feedback type="invalid">
                                            {errors.employee_id}
                                        </Form.Control.Feedback>
                                    )}
                                    {selectedEmployee && (
                                        <div className="d-flex align-items-center mt-2">
                                            <ImageShow 
                                                url={selectedEmployee.staff?.[0]?.profile_photo_url} 
                                                width={30} 
                                                height={30}
                                                style={{ borderRadius: '50%', marginRight: '10px' }}
                                            />
                                            <div>
                                                <small className="text-muted">
                                                    {selectedEmployee.department} - {selectedEmployee.designation}
                                                </small>
                                            </div>
                                        </div>
                                    )}
                                </Form.Group>
                            </Col>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Date *</Form.Label>
                                    <Form.Control
                                        type="date"
                                        name="date"
                                        className="form-control"
                                        value={formData.date}
                                        onChange={handleInputChange}
                                        isInvalid={!!errors.date}
                                    />
                                    {errors.date && (
                                        <Form.Control.Feedback type="invalid">
                                            {errors.date}
                                        </Form.Control.Feedback>
                                    )}
                                </Form.Group>
                            </Col>
                        </Row>

                        <Row>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Status *</Form.Label>
                                    <Form.Select
                                        name="status"
                                        className="form-control"
                                        value={formData.status}
                                        onChange={handleInputChange}
                                    >
                                        {attendanceStatuses.map(status => (
                                            <option key={status.value} value={status.value}>
                                                {status.label}
                                            </option>
                                        ))}
                                    </Form.Select>
                                </Form.Group>
                            </Col>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Check In</Form.Label>
                                    <Form.Control
                                        type="time"
                                        name="check_in"
                                        className="form-control"
                                        value={formData.check_in}
                                        onChange={handleInputChange}
                                        isInvalid={!!errors.check_in}
                                    />
                                    {errors.check_in && (
                                        <Form.Control.Feedback type="invalid">
                                            {errors.check_in}
                                        </Form.Control.Feedback>
                                    )}
                                </Form.Group>
                            </Col>
                        </Row>

                        <Row>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Check Out</Form.Label>
                                    <Form.Control
                                        type="time"
                                        name="check_out"
                                        className="form-control"
                                        value={formData.check_out}
                                        onChange={handleInputChange}
                                        isInvalid={!!errors.check_out}
                                    />
                                    {errors.check_out && (
                                        <Form.Control.Feedback type="invalid">
                                            {errors.check_out}
                                        </Form.Control.Feedback>
                                    )}
                                </Form.Group>
                            </Col>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Total Hours</Form.Label>
                                    <Form.Control
                                        type="text"
                                        name="total_hours"
                                        className="form-control"
                                        value={formData.total_hours}
                                        readOnly
                                        style={{ backgroundColor: '#f8f9fa' }}
                                    />
                                    <Form.Text className="text-muted">
                                        {formData.total_hours ? 
                                            `Auto-calculated: ${Math.floor(parseFloat(formData.total_hours))}h ${Math.round((parseFloat(formData.total_hours) % 1) * 60)}m` : 
                                            'Auto-calculated'
                                        }
                                    </Form.Text>
                                </Form.Group>
                            </Col>
                        </Row>

                        <Row>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Break Start</Form.Label>
                                    <Form.Control
                                        type="time"
                                        name="break_start"
                                        className="form-control"
                                        value={formData.break_start}
                                        onChange={handleInputChange}
                                    />
                                </Form.Group>
                            </Col>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Break End</Form.Label>
                                    <Form.Control
                                        type="time"
                                        name="break_end"
                                        className="form-control"
                                        value={formData.break_end}
                                        onChange={handleInputChange}
                                        isInvalid={!!errors.break_end}
                                    />
                                    {errors.break_end && (
                                        <Form.Control.Feedback type="invalid">
                                            {errors.break_end}
                                        </Form.Control.Feedback>
                                    )}
                                </Form.Group>
                            </Col>
                        </Row>

                        <Row>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Overtime Hours</Form.Label>
                                    <Form.Control
                                        type="text"
                                        name="overtime_hours"
                                        className="form-control"
                                        value={formData.overtime_hours}
                                        readOnly
                                        style={{ backgroundColor: '#f8f9fa' }}
                                    />
                                    <Form.Text className="text-muted">
                                        {formData.overtime_hours && parseFloat(formData.overtime_hours) > 0 ? 
                                            `Auto-calculated: ${Math.floor(parseFloat(formData.overtime_hours))}h ${Math.round((parseFloat(formData.overtime_hours) % 1) * 60)}m overtime` : 
                                            'Auto-calculated (8h standard)'
                                        }
                                    </Form.Text>
                                </Form.Group>
                            </Col>
                        </Row>

                        <Form.Group className="mb-3">
                            <Form.Label>Notes</Form.Label>
                            <Form.Control
                                as="textarea"
                                rows={3}
                                name="notes"
                                className="form-control"
                                value={formData.notes}
                                onChange={handleInputChange}
                                placeholder="Add any notes about attendance..."
                            />
                        </Form.Group>

                        {Object.keys(errors).length > 0 && (
                            <Alert variant="danger">
                                <strong>Please fix the following errors:</strong>
                                <ul className="mb-0 mt-2">
                                    {Object.entries(errors).map(([field, error]) => (
                                        <li key={field}>{error}</li>
                                    ))}
                                </ul>
                            </Alert>
                        )}
                    </Form>
                ) : (
                    <div>
                        <div className="mb-3">
                            <Form.Label>Date for All Employees</Form.Label>
                            <Form.Control
                                type="date"
                                value={bulkData[0]?.date || ''}
                                onChange={(e) => {
                                    const updatedBulkData = bulkData.map(item => ({
                                        ...item,
                                        date: e.target.value
                                    }));
                                    setBulkData(updatedBulkData);
                                }}
                                className="form-control"
                            />
                        </div>

                        <div className="table-responsive">
                            <Table striped bordered hover size="sm">
                                <thead>
                                    <tr>
                                        <th>Employee</th>
                                        <th>Status</th>
                                        <th>Check In</th>
                                        <th>Check Out</th>
                                        <th>Notes</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {bulkData.map((item, index) => (
                                        <tr key={index}>
                                            <td>
                                                <div className="fw-bold">{item.employee_name}</div>
                                            </td>
                                            <td>
                                                <Form.Select
                                                    value={item.status}
                                                    onChange={(e) => handleBulkInputChange(index, 'status', e.target.value)}
                                                    className="form-control form-control-sm"
                                                >
                                                    {attendanceStatuses.map(status => (
                                                        <option key={status.value} value={status.value}>
                                                            {status.label}
                                                        </option>
                                                    ))}
                                                </Form.Select>
                                            </td>
                                            <td>
                                                <Form.Control
                                                    type="time"
                                                    value={item.check_in}
                                                    onChange={(e) => handleBulkInputChange(index, 'check_in', e.target.value)}
                                                    className="form-control form-control-sm"
                                                />
                                            </td>
                                            <td>
                                                <Form.Control
                                                    type="time"
                                                    value={item.check_out}
                                                    onChange={(e) => handleBulkInputChange(index, 'check_out', e.target.value)}
                                                    className="form-control form-control-sm"
                                                />
                                            </td>
                                            <td>
                                                <Form.Control
                                                    type="text"
                                                    value={item.notes}
                                                    onChange={(e) => handleBulkInputChange(index, 'notes', e.target.value)}
                                                    className="form-control form-control-sm"
                                                    placeholder="Notes"
                                                />
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>
                        </div>
                    </div>
                )}
            </Modal.Body>
            <Modal.Footer>
                <div className="d-flex justify-content-end w-100" style={{gap: '10px'}}>
                    <div>
                        <Button 
                            variant="outline-secondary" 
                            onClick={() => setBulkMode(!bulkMode)}
                            disabled={loading}
                        >
                            {bulkMode ? 'Single Entry' : 'Bulk Entry'}
                        </Button>
                    </div>
                    <div className="d-flex" style={{gap: '10px'}}>
                        <Button
                            variant="secondary"
                            onClick={() => {
                                onHide();
                                setBulkMode(false);
                                setFormData({
                                    employee_id: '',
                                    date: '',
                                    check_in: '',
                                    check_out: '',
                                    break_start: '',
                                    break_end: '',
                                    total_hours: '',
                                    overtime_hours: '',
                                    status: 'present',
                                    notes: ''
                                });
                            }}
                            disabled={loading}
                        >
                            Cancel
                        </Button>
                        <Button 
                            variant="primary" 
                            onClick={handleSubmit}
                            disabled={loading}
                            className="ms-2"
                        >
                            {loading ? (
                                <>
                                    <Spinner animation="border" size="sm" className="me-2" />
                                    {bulkMode ? 'Adding...' : (editingAttendance ? 'Updating...' : 'Adding...')}
                                </>
                            ) : (
                                bulkMode ? 'Add Bulk Attendance' : (editingAttendance ? 'Update Attendance' : 'Add Attendance')
                            )}
                        </Button>
                    </div>
                </div>
            </Modal.Footer>
        </Modal>
    );
};

export default AddAttendanceModal;
